import React, {FC, ReactElement, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import './AnimatedList.scss';

export type animatedListProps = {
    listItems: any,
    mediaType: string,
    spaceBetweenItems: number,
    bulletsSize: number,
    bulletsColor: string,
    speed: string,
    htmlTag: string,
    fontSize: number,
    color: string,
    lineHeight: number,
    textAlignment: string,
    textDirection: string,
    anchorName: string,
    editorPlugins: any
}

type animatedListElementProps = {
    bulletsSize: number,
    spaceBetweenItems: number,
    fontSize: number,
    lineHeight: number,
    color: string,
    textDirection: string,
    textAlignment: string,
    minimalHeight:number
}


export const AnimatedListElement = styled.div<animatedListElementProps>`
&#animatedListElement {
    .listWrapper{
         ${props => props.textDirection === 'rtl' && `
              direction: rtl;
              text-align: right;
              .item {
                .icon {
                   margin-right: 0;              
                   margin-left: 12px;              
                }
              }
        `};
        .item {
           ${props => props.spaceBetweenItems && `
             margin-bottom: ${props.spaceBetweenItems + 'px'}
          `}; 
           .icon {
               ${props => props.bulletsSize && `
                  .images {
                    width: ${props.bulletsSize + 'px'};
                    height: ${props.bulletsSize + 'px'};
                  }
                  .material-icons {   
                      font-size: ${props.bulletsSize + 'px'};
                  }
               `}; 
                   
               ${props => props.fontSize && `
                    .bullets, .numbers {   
                        font-size: ${props.fontSize + 'px'};
                    }
               `};     
               ${props => props.color && `
                 .bullets, .numbers { 
                    color: ${props.color};
                 }
              `};
               ${props => props.minimalHeight && `
                    .bullets, .numbers {   
                        min-height: ${props.minimalHeight + 'px'};
                        ${props.fontSize && `
                            font-size: ${props.fontSize + 'px'}
                        `};
                        ${props.lineHeight && `
                            line-height: ${props.lineHeight}
                        `};
                    }
               `};            
           }
           .content, .content p {
                &.icons, &.images {
                    ${props => props.bulletsSize && `
                            min-height: ${props.bulletsSize + 'px'};
                            align-items:center;
                   `}; 
               }
               ${props => props.fontSize && `
                 font-size: ${props.fontSize + 'px'}
              `};
               ${props => props.lineHeight && `
                 line-height: ${props.lineHeight}
              `};
               ${props => props.color && `
                 color: ${props.color}
              `};
               ${props => props.textAlignment && `
                 text-align: ${props.textAlignment}
              `};
              ${props => props.textAlignment === 'right' && props.textDirection === 'ltr' && `
                 justify-content: end;
              `};
              ${props => props.textAlignment === 'right' && props.textDirection === 'rtl' && `
                 justify-content: start;
              `};
              ${props => props.textAlignment === 'left' && props.textDirection === 'ltr' && `
                 justify-content: start;         
              `};
              ${props => props.textAlignment === 'left' && props.textDirection === 'rtl' && `   
                 justify-content: end;         
              `};
               ${props => props.textAlignment === 'center' && `
                 justify-content: center;
              `};
          } 
        }
    }
}
`;


const AnimatedList: FC<animatedListProps> = ({...props}): ReactElement => {

    const defaultImageSrc = environmentLink + '/assets/images/imagePlaceholder.jpg';
    const elementRef = useRef<any>(null);
    const options = {
        root: null,
        rootMargin: '0px',
        threshold: [0.1]
    };


    useEffect(() => {
        const observer = new IntersectionObserver(callbackFunction, options);
        if (elementRef.current) observer.observe(elementRef.current);

        return () => {
            if (elementRef.current) observer.unobserve(elementRef.current);
        };
    }, [elementRef, options, props.listItems]);

    useEffect(() => {
        getMinimalHeight();
    }, [props.lineHeight]);


    const callbackFunction = (entries: any) => {
        const [entry] = entries;
        if (entry.isIntersecting) return initiateElementAnimation();
    };

    function initiateElementAnimation() {
        if (!props.listItems || !props.listItems.length || !elementRef || !elementRef.current) return;
        const parentElement = elementRef.current;
        const listChildes = parentElement.children;
        const delay_time = getAnimationSpeed();
        for (let i = 0; i < listChildes.length; i++) {
            setTimeout(()=>{
               updateElementsClasses(listChildes, i, delay_time);
            },i * delay_time);
        }
    }

    function updateElementsClasses(listChildes: any, index:number, delay_time: number) {
        let item = listChildes[index];
        item.classList.add('blur');
        item.classList.add('selected');
        if (index + 1 === listChildes.length) {
            for (let g = 0; g < listChildes.length; g++) {
                setTimeout(() => {
                let element = listChildes[g];
                element.classList.remove('blur');
                }, (index +1 ) * delay_time);
            }
        }
    }

    function getAnimationSpeed() {
        if (props.speed === 'slow') return 2000;
        if (props.speed === 'medium') return 1000;
        if (props.speed === 'fast') return 700;
        return 1000;
    }

    function getMinimalHeight() {
        let minHeight = 100;
        let contents:any = document.querySelectorAll('.listWrapper .item .content');
        if(!contents || !contents.length) return 0;
        for (const content of contents) {
            if(content.clientHeight < minHeight) minHeight = content.clientHeight;
        }
        return minHeight;
    }

    return (
        <AnimatedListElement id={'animatedListElement'} bulletsSize={props.bulletsSize} spaceBetweenItems={props.spaceBetweenItems}
                             fontSize={props.fontSize||14} color={props.color} textDirection={props.textDirection} textAlignment={props.textAlignment} lineHeight={props.lineHeight} minimalHeight={getMinimalHeight()}>
            {
                props.listItems && props.listItems.length && <div className={'listWrapper'} ref={elementRef}>
                    {
                        props.listItems.map((item: any, index: number) => {
                            return (
                                <div className={'item'} key={index}>
                                    <div className="icon">
                                        {
                                            props.mediaType === 'icons' && <div className={'option material-icons'} style={{"color":item.selectedColor}}>{item.icon}</div>
                                        }
                                        {
                                            props.mediaType === 'bullets' && <div className={'option bullets'}>
                                                <div className="bullet"></div>
                                            </div>
                                        }
                                        {
                                            props.mediaType === 'numbers' && <div className={'option numbers'}>{(index+1)}</div>
                                        }
                                        {
                                            props.mediaType === 'images' && <div className={'option images'}><img src={item.imgSrc || defaultImageSrc}/></div>
                                        }
                                    </div>
                                    <div className={"content " + props.mediaType} dangerouslySetInnerHTML={{__html: item.content}}></div>
                                </div>
                            );
                        })
                    }
                </div>
            }
        </AnimatedListElement>
    );
};


let settings: any;
let SettingsData;
const environmentLink: any = window.getEnvironmentLink();
try {
    SettingsData = require('../../../settings-data').default;
    // do stuff
} catch (ex) {
    SettingsData = null;
}
if (SettingsData) {
    const settingsClass = new SettingsData();
    const allSettings = settingsClass.getAllSettings();
    settings = [
        [//group
            {
                settingName: 'HorizontalAlignment',
            },
            {
                settingName: 'AnimatedList',
                title: 'Animated List',
                icon: environmentLink + '/assets/images/tabsContent.svg',
                isShowTitle: false,
                hasDrop: true,
                dropWidth: 'popup',
                removePadding: true,
                controls: [
                    {
                        controlName: 'AnimatedList',
                        property: 'listItems',
                        title: 'Animated List',
                        icon: environmentLink + '/assets/images/tabsContent.svg'
                    },
                ],
            },
            {
                settingName: 'AnimatedSettings',
                title: 'Animated Settings',
                icon: environmentLink + '/assets/images/BulletsStyle.svg',
                isShowTitle: false,
                hasDrop: true,
                dropWidth: 'big',
                removePadding: true,
                controls: [
                    {
                        controlName: 'ImageSelect',
                        property: 'mediaType',
                        title: 'Bullet style',
                        separateLine: true,
                        padding: '16px',
                        margin: '0',
                        presetData: [
                            {
                                icon: null,
                                value: 'None',
                                width: 20,
                                isValuesInsideBox: true
                            },
                            {
                                icon: environmentLink + '/assets/images/mediaType1.svg',
                                value: 'bullets',
                                width: 20
                            },
                            {
                                icon: environmentLink + '/assets/images/mediaType2.svg',
                                value: 'numbers',
                                width: 20
                            },
                            {
                                icon: environmentLink + '/assets/images/mediaType3.svg',
                                value: 'icons',
                                width: 20
                            },
                            {
                                icon: environmentLink + '/assets/images/mediaType4.svg',
                                value: 'images',
                                width: 20
                            },
                        ],
                        isCheckButton: true,
                    },
                    {
                        controlName: 'Slider',
                        property: 'spaceBetweenItems',
                        title: 'Spacing between items',
                        separateLine: true,
                        padding: '16px',
                        margin: '0',
                        suffix: 'px',
                        defaultValue: 24,
                        min: 10,
                        max: 150,
                        step: 1,
                    },
                    {
                        controlName: 'Slider',
                        property: 'bulletsSize',
                        title: 'Bullets Size',
                        separateLine: false,
                        padding: '16px',
                        margin: '0',
                        suffix: 'px',
                        defaultValue: 6,
                        min: 6,
                        max: 60,
                        step: 1,
                        enable: {
                            property: 'mediaType',
                            values: ['icons', 'images']
                        }
                    },
                ],
            },
            {
                settingName: 'Speed',
            },
        ],
        [
            {
                settingName: 'FontSize',
            },
            {
                settingName: 'TitleColor',
                controls: [
                    {
                        controlName: 'CustomColors',
                        property: 'color',
                        title: '',
                        defaultValue: '#12033E',
                        isGradient: false
                    },
                ],
            },
            {
                settingName: 'TextAlignment'
            },
            {
                settingName: 'LineHeight'
            },
            {
                settingName: 'TextDirection'
            }
        ],
        [
            {
                settingName: 'Anchor',
            }
        ]
    ];
}

const options = [
    {
        name: 'editAnimatedList',
        title: 'Edit list',
        icon: environmentLink + '/assets/images/editList.svg',
        action: 'editAnimatedList',
        settingTitle: 'Animated List'
    }
];


export default {
    AnimatedList,
    settings,
    options
};


